import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtEur } from "../lib/utils";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { FileThumb } from "../components/FileThumb";
import { Sparkle, CheckCircle, FloppyDisk, Gear } from "@phosphor-icons/react";

const QUANTITIES = [100, 200, 500, 1000];
const LABELS = {
  thank_you_card: "Thank You Card / Flyer", sticker: "Adesivi Brand",
  busta_tshirt: "Busta T-Shirt (25x30 cm)", busta_felpa: "Busta Felpa (30x35 cm)",
};
const PRICING_TYPES = ["thank_you_card", "sticker", "busta_tshirt", "busta_felpa"];
const STATUS_META = {
  pending_payment: { cls: "tf-status-neutral", label: "In attesa pagamento" },
  in_production: { cls: "tf-status-warning", label: "In produzione" },
  available: { cls: "tf-status-success", label: "Disponibile" },
  exhausted: { cls: "tf-status-error", label: "Esaurito" },
};

export default function AdminBranding() {
  const [orders, setOrders] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);

  const load = () => {
    api.get("/admin/branding/orders").then(({ data }) => setOrders(data));
    api.get("/admin/branding/pricing").then(({ data }) => setPricing(data));
  };
  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    try {
      await api.post(`/admin/branding/orders/${id}/activate`);
      toast.success("Ordine attivato: disponibile per il cliente");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const updatePrice = (type, qty, value) => {
    setPricing({ ...pricing, [type]: { ...pricing[type], [qty]: parseFloat(value) || 0 } });
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await api.put("/admin/branding/pricing", {
        thank_you_card: pricing.thank_you_card, sticker: pricing.sticker,
        busta_tshirt: pricing.busta_tshirt, busta_felpa: pricing.busta_felpa,
        service_fee: pricing.service_fee,
      });
      toast.success("Prezzi aggiornati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSavingPricing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-branding">
      <PageHeader
        eyebrow="Servizi"
        title="Branding & Packaging"
        subtitle="Ordini di produzione Thank You Card / Flyer, Adesivi Brand e Buste Personalizzate di tutti i clienti. Attiva lo stock quando arriva in magazzino."
        chip="tf-chip-peach"
        icon={Sparkle}
      />

      {pricing && (
        <div className="tf-card p-5" data-testid="branding-pricing-editor">
          <div className="flex items-center justify-between mb-4">
            <div className="tf-label flex items-center gap-2"><Gear size={12} weight="bold" /> Prezzi di produzione (placeholder, modificabili)</div>
            <button onClick={savePricing} disabled={savingPricing} className="tf-btn tf-btn-primary !py-1.5 text-xs" data-testid="save-pricing-btn">
              <FloppyDisk size={14} weight="bold" /> Salva prezzi
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {PRICING_TYPES.map((type) => (
              <div key={type}>
                <div className="font-display font-bold text-sm mb-2">{LABELS[type]}</div>
                <div className="grid grid-cols-2 gap-2">
                  {QUANTITIES.map((q) => (
                    <div key={q} className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-[var(--tf-muted)] pointer-events-none">{q}pz €</span>
                      <input
                        type="number" step="0.01"
                        value={pricing[type][String(q)] ?? ""}
                        onChange={(e) => updatePrice(type, String(q), e.target.value)}
                        className="tf-input !py-1.5 !pl-16 text-right tf-numeric w-full"
                        data-testid={`pricing-${type}-${q}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 max-w-xs">
            <div className="tf-label mb-1.5">Costo servizio per ordine (IVA esclusa)</div>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--tf-muted)] pointer-events-none">€</span>
              <input
                type="number" step="0.01"
                value={pricing.service_fee}
                onChange={(e) => setPricing({ ...pricing, service_fee: parseFloat(e.target.value) || 0 })}
                className="tf-input !py-1.5 !pl-6 text-right tf-numeric w-full"
                data-testid="pricing-service-fee"
              />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {orders.length === 0 && (
          <div className="tf-card p-8 text-center text-sm text-[var(--tf-muted)]">Nessun ordine Branding & Packaging ancora.</div>
        )}
        {orders.map((o) => {
          const meta = STATUS_META[o.status] || STATUS_META.pending_payment;
          return (
            <div key={o.id} className="tf-card p-4 flex flex-wrap items-center gap-4" data-testid={`admin-branding-order-${o.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold text-sm">{o.company_name}</div>
                <div className="text-xs text-[var(--tf-muted)] mt-0.5">
                  {LABELS[o.product_type]} · {o.quantity} pz · {fmtEur(o.price)}
                  {o.dimensions && <> · {o.dimensions}</>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {o.front_url && (
                  <a href={o.front_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1" data-testid={`file-front-${o.id}`}>
                    <FileThumb url={o.front_url} size={36} testid={`file-front-thumb-${o.id}`} />
                    <span className="text-[9px] text-[var(--tf-muted)] font-medium">Fronte</span>
                  </a>
                )}
                {o.back_url && (
                  <a href={o.back_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1" data-testid={`file-back-${o.id}`}>
                    <FileThumb url={o.back_url} size={36} testid={`file-back-thumb-${o.id}`} />
                    <span className="text-[9px] text-[var(--tf-muted)] font-medium">Retro</span>
                  </a>
                )}
                {o.graphic_url && (
                  <a href={o.graphic_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1" data-testid={`file-graphic-${o.id}`}>
                    <FileThumb url={o.graphic_url} size={36} testid={`file-graphic-thumb-${o.id}`} />
                    <span className="text-[9px] text-[var(--tf-muted)] font-medium">Grafica</span>
                  </a>
                )}
              </div>
              {(o.status === "available" || o.status === "exhausted") && (
                <div className="text-xs font-display font-bold tf-numeric text-[var(--tf-muted)]">
                  {o.stock_remaining} / {o.stock_total} rimanenti
                </div>
              )}
              <span className={`tf-status-pill ${meta.cls}`} data-testid={`admin-branding-status-${o.id}`}>{meta.label}</span>
              {o.status === "in_production" && (
                <button onClick={() => activate(o.id)} className="tf-btn tf-btn-primary !py-1.5 text-xs" data-testid={`activate-btn-${o.id}`}>
                  <CheckCircle size={14} weight="bold" /> Conferma arrivo in magazzino
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
